import clsx from 'clsx';
import {FieldValues} from 'react-hook-form';
import {SPECIAL_SYMBOLS_MAP, TRANSLITERATE_CYRILLIC_MAP} from '@/const';
import {IDefaultErrorObject} from '@/interfaces';
import ToastifyService, {TOAST_TYPES} from '@/services/ToastifyService';
import {IRequestParams, IUseHandleGeneralErrors} from '@/api/interfaces';
import {IArticle} from '@/queries/articles/interfaces';
import {TMonitoringStat} from '@/queries/monitoring/interfaces';

export const clearDomForApp = (): HTMLElement => {
    const html = document.querySelector('html') as HTMLHtmlElement;
    const body = document.querySelector('body') as HTMLBodyElement;
    const initialStyle = document.querySelector('[data-initial-style]') as HTMLStyleElement;
    const root = document.createElement('div') as HTMLDivElement;

    root.id = 'root';
    html.removeAttribute('class');
    body.removeAttribute('class');
    body.innerHTML = '';
    body.appendChild(root);
    initialStyle?.remove();

    return root;
};

export const timeout = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

export const createErrorObj = (error: IDefaultErrorObject) => {
    if (!error) return null;

    return {
        message: error?.response?.data?.message || error?.message,
        status: error?.status,
        validation: error?.response?.data?.data?.params
    };
};

export const showGeneralError = ({error = null, message = null}: IUseHandleGeneralErrors) => {
    if (error) {
        const errorObj = createErrorObj(error);

        ToastifyService.toast(
            message ?? (errorObj?.message || 'Something went wrong. Please, reload page and try again!'),
            TOAST_TYPES.error
        );
    }
};

export const getURLParamsString = (params: IRequestParams) => {
    const searchParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
        const currentParams = params?.[key];
        if (currentParams) {
            if (Array.isArray(currentParams)) {
                key === '_fields' || key === '_embed'
                    ? searchParams.append(key, currentParams?.join(','))
                    : currentParams?.forEach(param => searchParams.append(key + '[]', param));
            } else {
                searchParams.append(key, String(currentParams));
            }
        }
    });

    return searchParams.toString();
};

export const copyToClipboard = async (text: string) => {
    if (!text || !navigator.clipboard) return;

    try {
        await navigator.clipboard.writeText(text);
        ToastifyService.toast('Copied to clipboard!', TOAST_TYPES.success);
    } catch (err) {
        console.error('Failed to copy code: ', err);
    }
};

export const formatNumber = (num: TMonitoringStat | undefined, maximumFractionDigits: number = 2) => {
    if (!num) return '0';
    return Number(num).toLocaleString('en-US', {maximumFractionDigits});
};

export const testEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const testUrl = (url: string) => /^https:\/\/[^\s/$.?#].[^\s]*\.[^\s]{2,}$/.test(url);

export const testSimpleUrl = (url: string) => /^[^\s/$.?#].[^\s]*\.[^\s]{2,}$/.test(url);

export function dirtyValues(formValues: FieldValues, dirtyFields: FieldValues | boolean): object {
    if (dirtyFields === true) return formValues;
    if (typeof dirtyFields === 'object' && dirtyFields !== null) {
        return Object.fromEntries(
            Object.entries(dirtyFields).map(([key, value]) => [key, dirtyValues(formValues[key], value)])
        );
    }
    return {};
}

export const removeProtocol = (url: string) => url.replace(/(^\w+:|^)\/\//, '').replace(/\/$/, '');

export const leaveOnlyFloats = (value: string) => (value ? value.replace(/[^\d.]+/g, '') : '');

export const replaceHttpWithHttps = (url: string) => {
    return url.includes('http://') ? url.replace('http://', 'https://') : url;
};

export const getRemoteUrls = (remote: IArticle['remote']) => {
    return Object.entries(remote || {})?.map(([_, value]) => value?.url || '');
};

export const getFlagEmoji = (countryCode: string) => {
    return [...countryCode.toUpperCase()]
        .map(char => String.fromCodePoint(127397 + char.charCodeAt(0)))
        .reduce((a, b) => `${a}${b}`);
};

export const isVideoSource = (url: string) => {
    if (!url) return false;
    const extension = url.split('.').pop()?.toLowerCase();
    return ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'mkv'].includes(extension || '');
};

export const sizeInMB = (size: number) => {
    if (!size) return 0;
    return Math.round(size / (1024 * 1024));
};

export const belowWidth = (width: number) => `(max-width: ${width}px)`;
export const aboveWidth = (width: number) => `(min-width: ${width}px)`;

export const encodeString = (str: string) => {
    return str.replaceAll(/[!@#$%^&*()\-_=+[\]{}\\|;:'",.<>/? ]/g, match => SPECIAL_SYMBOLS_MAP[match]);
};

export const decodeString = (encodedStr: string) => {
    let decodedStr = encodedStr;
    for (const [char, encoded] of Object.entries(SPECIAL_SYMBOLS_MAP)) {
        decodedStr = decodedStr.replaceAll(encoded, char);
    }
    return decodedStr;
};

export const getEpcCellShade = (epcVal: number) => {
    return {
        className: clsx(
            !!epcVal && epcVal <= 0.5 && 'epcCellShade4',
            epcVal > 0.5 && epcVal <= 1 && 'epcCellShade3',
            epcVal > 1 && epcVal <= 5 && 'epcCellShade2',
            epcVal > 5 && 'epcCellShade1'
        )
    };
};

export const getGeoColorByValue = (value: number, maxValue: number) => {
    const ratio = value / maxValue;
    const minColor = {r: 199, g: 224, b: 254};
    const maxColor = {r: 41, g: 138, b: 251};

    const r = Math.round(minColor.r + ratio * (maxColor.r - minColor.r));
    const g = Math.round(minColor.g + ratio * (maxColor.g - minColor.g));
    const b = Math.round(minColor.b + ratio * (maxColor.b - minColor.b));

    return `rgb(${r}, ${g}, ${b})`;
};

export const transliterateCyrillic = (str: string) => {
    if (!str) return str;

    return str
        ?.split('')
        ?.map(char => TRANSLITERATE_CYRILLIC_MAP?.[char] || char)
        ?.join('');
};

export const parseArticleUrl = (remoteUrl: string | undefined) => {
    if (!remoteUrl) return '';

    try {
        const url = testSimpleUrl(remoteUrl) && new URL(remoteUrl);
        return url instanceof URL ? url.href.replace(url.origin + '/', '')?.replace('dsr?q=', '') : remoteUrl;
    } catch (e) {
        console.error(e);
        return remoteUrl;
    }
};
